import { cn, getAccessCode, getNexonUrl, GetSafeRedirectUrl, getSearchParams } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import IDPasswordLogin from "@/components/tabs/login/IDPasswordLogin";
import OneTimeLogin from "@/components/tabs/login/OneTimeLogin";
import QRLogin from "../components/tabs/login/QRLogin";
import NexonLogoSvg from "@assets/images/nexon_logo.svg";
import TestEnvIcon from "@assets/images/ico_test_env.svg";
import NaverPromotionBanner from "@assets/images/npay_banner.png";
import { useNavigate } from "@tanstack/react-router";
import CookieStore from "@/stores/CookieStore";
import { LOGIN_TAB, LOGIN_TAB_SEARCH_PARAM, ONE_TIME_LOGIN_TAB, QR_LOGIN_TAB } from "@/consts";
import { withLog } from "@/lib/a2s";
import { useEffect, useState } from "react";
import { getIsGetoAccessCode } from "@/lib/geto";
import { getIsStellaAccessCode } from "@/lib/stella";

type Props = React.HTMLAttributes<HTMLDivElement>;

// 스텔라 진입: sec 무시, 넥슨ID 로그인 고정
const getInitialSection = () => (getIsStellaAccessCode() ? LOGIN_TAB : getSearchParams(LOGIN_TAB_SEARCH_PARAM) || LOGIN_TAB);

const LoginPage = ({ className }: Props) => {
  const [section, setSection] = useState(getInitialSection);
  const navigate = useNavigate();

  const usetpa = CookieStore((state) => state.usetpa);
  const signup = CookieStore((state) => state.signup);

  const isGeto = getIsGetoAccessCode();
  const isStella = getIsStellaAccessCode();
  // GETO·스텔라 진입: 로고 링크 막기
  const hideExitLinks = isGeto || isStella;

  const handleLogoClick = () => {
    if (hideExitLinks) {
      return;
    }
    window.location.href = getNexonUrl();
  };

  const handleTabClick = (tab: string) => {
    const LinkSearchOptions = {
      redirect: GetSafeRedirectUrl(),
      usetpa: usetpa,
      accesscode: getAccessCode(),
      signup: signup,
    };

    setSection(tab);
    navigate({
      to: ".",
      search: { ...LinkSearchOptions, sec: tab },
    });
  };

  useEffect(() => {
    const handlePopstate = () => {
      setSection(getInitialSection());
    };

    const handlePageshow = () => {
      nest.validateSession({
        success: () => {
          location.replace(GetSafeRedirectUrl());
        },
        failure: () => {},
      });
    };

    window.addEventListener("popstate", handlePopstate);
    window.addEventListener("pageshow", handlePageshow);
    return () => {
      window.removeEventListener("popstate", handlePopstate);
      window.removeEventListener("pageshow", handlePageshow);
    };
  }, []);

  return (
    <>
      <a
        onClick={withLog("LOGIN0002", handleLogoClick)}
        className={cn("mb-8 flex cursor-pointer md:mb-10", hideExitLinks && "cursor-default")}
      >
        {["development"].includes(import.meta.env.VITE_NODE_ENV) && <img src={TestEnvIcon} width={20} height={20} className="mr-1" />}
        <img src={NexonLogoSvg} width={138} height={22} className="w-[8.625rem]" />
      </a>
      <Tabs className={cn("flex w-full flex-col justify-center", className)} value={section}>
        {/* 스텔라 진입: 탭 바 숨김(넥슨ID 로그인만 노출) */}
        {!isStella && (
          <TabsList className="mx-auto max-w-[350px]">
            <a className="inline-flex flex-1" onClick={withLog("LOGIN0003", () => handleTabClick(LOGIN_TAB))}>
              <TabsTrigger value={LOGIN_TAB}>넥슨ID 로그인</TabsTrigger>
            </a>
            <a className="inline-flex flex-1" onClick={withLog("LOGIN0004", () => handleTabClick(ONE_TIME_LOGIN_TAB))}>
              <TabsTrigger value={ONE_TIME_LOGIN_TAB}>일회용 로그인</TabsTrigger>
            </a>
            <a className="inline-flex flex-1" onClick={withLog("LOGIN0005", () => handleTabClick(QR_LOGIN_TAB))}>
              <TabsTrigger value={QR_LOGIN_TAB}>QR 로그인</TabsTrigger>
            </a>
          </TabsList>
        )}
        <TabsContent value={LOGIN_TAB}>
          <IDPasswordLogin />
        </TabsContent>
        <TabsContent value={ONE_TIME_LOGIN_TAB}>
          <OneTimeLogin />
        </TabsContent>
        <TabsContent value={QR_LOGIN_TAB}>
          <QRLogin />
        </TabsContent>
      </Tabs>
      {/* 네이버 계정전환 프로모션, 스텔라 진입 시 숨김 */}
      {!isStella && (
        <section className="mt-[3.75rem]" onClick={withLog("LOGIN0035", () => {})}>
          <a href={import.meta.env.VITE_NAVER_PROMOTION_URL} target="_self">
            <img src={NaverPromotionBanner} alt="네이버 계정전환 프로모션 배너" />
          </a>
        </section>
      )}
    </>
  );
};

export default LoginPage;
